import { Vec2 } from "planck-js";
import { GameScene } from "../scenes/GameScene";
import { DestroyInWall } from "./DestroyInWall";
import { DestroyOffscreen } from "./DestroyOffscreen";
import { Environment } from "./FlyState";
import { PlanckBodyComponent, PlanckBodyType } from "./PlanckBodyComponent";
import { Player } from "./Player";
import { PoolGameObject } from "./PoolGameObject";
import { SpineAnimation } from "./SpineAnimation";

export class MovingObject extends PoolGameObject {
	gameScene: GameScene;

	carSpines = ["metropolis-car-old", "metropolis-car-modern"];
	oldCarSkins = ["red", "white"];
	newCarSkins = ["blue", "yellow"];

	ghostSpines = ["scooby-ghost-purple", "scooby-ghost-tan"];
	clownSpines = ["gotham-clown-green", "gotham-clown-pink", "gotham-clown-mohawk"];

	hitboxHeight: number;
	hitbox: Phaser.GameObjects.Image;
	planckBody: PlanckBodyComponent;
	currentEnvironment: Environment;

	constructor(scene: GameScene, environment: Environment) {
		super(scene);
		this.gameScene = scene;
		this.currentEnvironment = environment;

		const width = 160;
		const height = this.hitboxHeight = 130;

		const img = this.hitbox = this.addImage("whiteSquare");
		img.alpha = 0;
		img.setDisplaySize(width, height);
		this.setSize(width, height);
		img.tint = 0xAAAAAA;

		this.planckBody = this.addComponent(new PlanckBodyComponent(this, PlanckBodyType.BOX, {
			x: this.x, y: this.y,
			width: this.hitbox.displayWidth, height: this.hitbox.displayHeight,
			worldScale: GameScene.worldScale,
			isKinematic: true,
			isSensor: false,
		}));
		this.planckBody.setActive(false);
		
		this.addComponent(new DestroyOffscreen(this, true, true, 250));
		this.addComponent(new DestroyInWall(this));

		let spine: SpineGameObject;
		let oy = 50;
		let key = "";

		switch (environment) {
			case Environment.METROPOLIS: {
				key = Phaser.Math.RND.pick(this.carSpines);
				spine = this.scene.add.spine(0, 0, key, "animation", true);
				if (key == "metropolis-car-old") {
					spine.setSkinByName(Phaser.Math.RND.pick(this.oldCarSkins));
				} else {
					spine.setSkinByName(Phaser.Math.RND.pick(this.newCarSkins));
					oy += 10;
				}
				break;
			}
			case Environment.HAUNTEDHOUSE: {
				key = Phaser.Math.RND.pick(this.ghostSpines);
				spine = this.scene.add.spine(0, 0, key, "animation", true);
				spine.scale = 0.4;
				oy += 0;
				break;
			}
			case Environment.GOTHAM: {
				key = Phaser.Math.RND.pick(this.clownSpines);
				spine = this.scene.add.spine(0, 0, key, "idle", true);
				spine.scale = 0.8;
				oy -= 30;
				break;
			}
			default: {
				key = "bedrock-stone";
				spine = this.scene.add.spine(0, 0, key, "roll", true);
				spine.setSkinByName("shade" + Phaser.Math.RND.between(1, 2).toString());
				break;
			}
		}
		this.addComponent(new SpineAnimation(this, spine, key, false, 0, oy));
	}

	poolSetup(x: number, y: number, speed: number): void {
		this.x = x;
		this.y = y - this.hitboxHeight * 0.4;

		this.planckBody.setActive(true);
		this.planckBody.setPosition(this.x, this.y);
		this.planckBody.velocity.x = speed;
	}

	poolDestroy(): void {
		this.planckBody.setActive(false);
	}

	playImpact(coinCount: number, target: Player): void {
		const spine = this.scene.add.spine(0, 0, "player-impact-puff", "animation", false);
		const spineContainer = this.scene.add.spineContainer(0, 0, spine);
		if (this.parentContainer) this.parentContainer.add(spineContainer);
		else this.scene.add.existing(spineContainer);
		spineContainer.setPosition(this.x, this.y);
		spine.once("complete", () => {
			spine.destroy();
			spineContainer.destroy();
		});

		for (let i = 0; i < coinCount; i++) {
			const sprite = this.scene.add.sprite(0, 0, "coin-sheet");
			sprite.play("coinIdle");

			const worldView = this.gameScene.cameraController.getRealWorldView();
			const xFrac = (this.x - worldView.left) / worldView.width;
			const yFrac = (this.y - worldView.top) / worldView.height;

			const screenPos = new Vec2(xFrac * this.scene.viewport.width, yFrac * this.scene.viewport.height);
			sprite.setPosition(screenPos.x, screenPos.y);
			this.gameScene.uiContainer.add(sprite);
			
			const xFrac2 = (target.x - worldView.left) / worldView.width;
			const yFrac2 = (target.y - worldView.top) / worldView.height;
			let playerPos = new Vec2(xFrac2 * this.scene.viewport.width, yFrac2 * this.scene.viewport.height);
	
			const gameScene = this.gameScene;
			const timeline = gameScene.tweens.createTimeline();
			let tween: Phaser.Tweens.Tween | undefined = undefined;
			timeline.add({
				targets: sprite,
				x: sprite.x + Phaser.Math.RND.between(-200,200),
				y: sprite.y + Phaser.Math.RND.between(-200, 0),
				duration: 200,
				ease: Phaser.Math.Easing.Quadratic.In,
				onComplete: () => {
					
					gameScene.ltGame.player.collectCoin();
					tween = gameScene.tweens.add({
						targets: sprite,
						alpha: 0,
						duration: 99999,
						onUpdate: () => {
							const xFrac2 = (target.x - worldView.left) / worldView.width;
							const yFrac2 = (target.y - worldView.top) / worldView.height;
							playerPos = new Vec2(xFrac2 * gameScene.viewport.width, yFrac2 * gameScene.viewport.height);
							sprite.y += (playerPos.y - sprite.y) / 5;
						}
					})
				},
			});
			timeline.add({
				targets: sprite,
				x: playerPos.x,
				duration: 200,
				ease: Phaser.Math.Easing.Quadratic.Out,
				onComplete: () => {
					if (i == 0) {
						sprite.play("coinBurst");
						sprite.once("animationcomplete", () =>{
							tween.stop();
							sprite.destroy();
						});
					} else {
						tween.stop();
						sprite.destroy();
					}
				}
			});
			timeline.play();
		}

		this.destroy();
	}
}